/**
 * Datasource remoto Services: abstração + implementação Firestore.
 * Services é subcoleção de users: users/{userId}/services/{serviceId}.
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  addDoc,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import { Service, ServiceRef, serviceSchema, UserRef } from '@/core/domain';

export interface ServiceRemoteDataSource {
  getById(userId: string, id: string): Promise<Service | null>;
  getAllByUserId(userId: string): Promise<Service[]>;
  getAll(userId: string): Promise<Service[]>;
  create(userId: string, service: Service): Promise<Service>;
  update(userId: string, id: string, service: Partial<Service>): Promise<Service>;
  delete(userId: string, id: string): Promise<void>;
}

function toService(id: string, data: Record<string, unknown>): Service {
  const { id: _ignoredIdFromFirestore, ...rest } = data;
  const parsed = serviceSchema.safeParse({ ...rest, id });
  if (!parsed.success) {
    console.error('[ServiceFirestoreDataSource] Invalid service from Firestore', {
      serviceId: id,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
        code: issue.code,
      })),
      rawData: data,
    });
    throw new Error('Invalid service from Firestore');
  }
  return parsed.data;
}

export class ServiceFirestoreDataSource implements ServiceRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  private serviceDocRef(userId: string, id: string) {
    return doc(this.getDb(), UserRef.collection, userId, ServiceRef.collection, id);
  }

  private servicesCollectionRef(userId: string) {
    return collection(this.getDb(), UserRef.collection, userId, ServiceRef.collection);
  }

  async getById(userId: string, id: string): Promise<Service | null> {
    const ref = this.serviceDocRef(userId, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toService(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAllByUserId(userId: string): Promise<Service[]> {
    const ref = this.servicesCollectionRef(userId);
    const snap = await getDocs(ref);
    return snap.docs.map((d) => toService(d.id, d.data() as Record<string, unknown>));
  }

  async getAll(userId: string): Promise<Service[]> {
    return this.getAllByUserId(userId);
  }

  async create(userId: string, service: Service): Promise<Service> {
    const ref = this.servicesCollectionRef(userId);
    const { id: _ignoredIdFromInput, ...servicePayload } = service;
    const docRef = await addDoc(ref, servicePayload);
    const id = docRef.id;
    await setDoc(docRef, { id }, { merge: true });
    return toService(id, { ...service, id });
  }

  async update(userId: string, id: string, service: Partial<Service>): Promise<Service> {
    const ref = this.serviceDocRef(userId, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Service not found');
    const current = toService(snap.id, snap.data() as Record<string, unknown>);
    const updated = { ...current, ...service, id };
    await setDoc(ref, updated);
    return updated;
  }

  async delete(userId: string, id: string): Promise<void> {
    const ref = this.serviceDocRef(userId, id);
    await deleteDoc(ref);
  }
}
